import { Injectable } from '@angular/core';
import { AppConfiguration, AppFeature, EnvironmentConfiguration, Stage } from './app-configuration';
import { ApiRouteDefinition, AuthConfig, HttpMethod } from '@auth0/auth0-angular';
import { IAMTenant, IamToggleService } from '@markant-platform/shared/iam-toggle/data-access';

@Injectable({
  providedIn: 'root',
})
export class AppConfigService {
  static appConfiguration: AppConfiguration;
  platformVersion = '1.10.12';
  public baseUrl = `${window.location.origin}/platform`;
  private env = (window as unknown as { env: EnvironmentConfiguration }).env || {};
  private baseAppConfiguration: AppConfiguration = {
    apiUrl: {
      auditService: '/audit/changeset',
      authorizerService: '/authorizer',
      leistungService: '/cartridge',
      bookingService: '/booking',
      userCartridgeService: '/cartridge/booked',
      priceRequestService: '/price-request',
      cartridgeGroupService: '/cartridge-group',
      userService: '/user',
      organizationService: '/organization',
      organizationDocumentService: '/storage',
      usageRightServiceUsageRight: '/usage-right-service/usage-right',
      usageRightServiceLeistung: '/usage-right-service/leistung',
      usageRightServiceOrganization: '/usage-right-service/usage-right/organization',
      notificationServiceBannerNotifications: '/banner-notifications',
      notificationServiceAudiences: '/audiences',
      userNotificationsService: '/user-notification',
      platformNewsService: '/news',
      contentService: '/content',
      logErrorService: '/log-error',
      registration: '/registration',
      campaign: '/campaign',
      furtherQualification: '/further-qualification',
      platformRequest: '/platform-request',
      userRegistration: '/user/registration',
      userOrganization: '/user/organization',
      userPlatformRequest: '/user/platform-request',
      userDownload: '/download',
      consentService: '/consent',
      legalDocumentService: '/legal-document',
      usageServiceAggregation: '/usage/aggregation',
      reportService: '/report',
      userServiceSupport: '/user-service/support',
      userWidgets: '/user-widgets',
    },
    auth: {} as AuthConfig,
    basicAuthCredentials: this.env.basicAuthCredentials,
    features: {
      captcha: this.env.features?.captchaEnabled,
      priceRequest: this.env.features?.priceRequestEnabled,
      consentDialogNormalChange: this.env.features?.consentDialogNormalChangeEnabled,
      cartridgeVariantOwnerReplacement: this.env.features?.cartridgeVariantOwnerReplacementEnabled,
      iamToggle: this.env.stage !== Stage.PROD ? 'true' : 'false',
      guidedTour: this.env.features?.guidedTourEnabled,
      roleLimits: this.env.features?.roleLimitsEnabled,
    },
    guidedTour: {
      orgAdminIntroduction: {
        highlightedServiceId: this.env.guidedTour?.orgAdminIntroduction.highlightedServiceId ?? '',
      },
    },
    hcaptchaSiteKey: this.env.hcaptchaSiteKey,
    logoutPageUrl: this.baseUrl + '/logout',
    notificationCheckInterval: this.env.notificationCheckInterval,
    platformVersion: this.platformVersion,
    publicEndpoints: [
      { route: '/registration', httpMethods: [HttpMethod.Post] },
      { route: '/registration/campaign/*', httpMethods: [HttpMethod.Post] },
      { route: '/registration/demo', httpMethods: [HttpMethod.Post] },
      { route: '/campaign/*', httpMethods: [HttpMethod.Get] },
      { route: '/registration/verify-email', httpMethods: [] },
      { route: '/user/registration/complete', httpMethods: [] },
      { route: '/content/footer-links', httpMethods: [] },
      { route: '/content/terms-of-use', httpMethods: [] },
      { route: '/content/privacy-notice', httpMethods: [] },
      { route: '/content/gtc', httpMethods: [] },
      { route: '/cartridge/*?scope=name', httpMethods: [] },
      { route: '/cartridge-group/*/name', httpMethods: [] },
      { route: '/user/mfa-select', httpMethods: [] },
    ],
    stage: this.env.stage,
    supportPortalUrl: this.env.supportPortalUrl,
    supportWidgetUrl: this.env.supportWidgetUrl,
    supportWidgetId: this.env.supportWidgetId,
  };

  constructor(private iamToggleService: IamToggleService) {}

  fetchAppConfiguration(): Promise<AppConfiguration> {
    let appConfiguration: AppConfiguration;
    const localDevAuthorizedBackendUris = ['/*', 'http://localhost:8180/api/*'];
    if (window.location.origin.startsWith('http://localhost') || window.location.origin.includes('.local')) {
      appConfiguration = this.baseAppConfiguration;
      appConfiguration.auth = this.prepareAuthConfig(localDevAuthorizedBackendUris);
    } else {
      const apiManagementBaseUrl = this.env.apiManagementBaseUrl || 'https://api.dev-platform-spoke.markant.services';
      const leistungServiceUrl = `${apiManagementBaseUrl}/leistung-service/cartridge`;
      const bookingServiceUrl = `${apiManagementBaseUrl}/leistung-service/booking`;
      const userCartridgeServiceUrl = `${apiManagementBaseUrl}/leistung-service/cartridge/booked`;
      const priceRequestServiceUrl = `${apiManagementBaseUrl}/leistung-service/price-request`;
      const cartridgeGroupServiceUrl = `${apiManagementBaseUrl}/leistung-service/cartridge-group`;
      const userServiceUrl = `${apiManagementBaseUrl}/user-service/user`;
      const usageRightServiceOrganizationUrl = `${apiManagementBaseUrl}/usage-right-service/usage-right/organization`;
      const usageRightServiceUsageRightUrl = `${apiManagementBaseUrl}/usage-right-service/usage-right`;
      const usageRightServiceLeistungUrl = `${apiManagementBaseUrl}/usage-right-service/leistung`;
      const organizationServiceUrl = `${apiManagementBaseUrl}/organization-service/organization`;
      const organizationDocumentServiceUrl = `${apiManagementBaseUrl}/organization-document-service/storage`;
      const auditServiceUrl = `${apiManagementBaseUrl}/audit-service/audit/changeset`;
      const authorizerServiceUrl = `${apiManagementBaseUrl}/authorizer-service`;
      const notificationServiceBannerNotificationsUrl = `${apiManagementBaseUrl}/notification-service/banner-notifications`;
      const notificationServiceAudiencesUrl = `${apiManagementBaseUrl}/notification-service/audiences`;
      const userNotificationsServiceUrl = `${apiManagementBaseUrl}/user-notification-service/user-notification`;
      const platformNewsServiceUrl = `${apiManagementBaseUrl}/news-platform-service/news`;
      const contentServiceUrl = `${apiManagementBaseUrl}/content-service/content`;
      const logErrorServiceUrl = `${apiManagementBaseUrl}/content-service/log-error`;
      const registrationUrl = `${apiManagementBaseUrl}/organization-service/registration`;
      const campaignUrl = `${apiManagementBaseUrl}/organization-service/campaign`;
      const furtherQualificationUrl = `${apiManagementBaseUrl}/organization-service/further-qualification`;
      const platformRequestUrl = `${apiManagementBaseUrl}/organization-service/platform-request`;
      const userDownloadUrl = `${apiManagementBaseUrl}/user-service/download`;
      const userRegistrationUrl = `${apiManagementBaseUrl}/user-service/registration`;
      const userOrganizationUrl = `${apiManagementBaseUrl}/user-service/organization`;
      const userPlatformRequestUrl = `${apiManagementBaseUrl}/user-service/platform-request`;
      const consentServiceUrl = `${apiManagementBaseUrl}/consent-service/consent`;
      const legalDocumentServiceUrl = `${apiManagementBaseUrl}/consent-service/legal-document`;
      const usageServiceAggregation = `${apiManagementBaseUrl}/usage-service/usage/aggregation`;
      const reportServiceUrl = `${apiManagementBaseUrl}/reporting-service/report`;
      const userServiceSupport = `${apiManagementBaseUrl}/user-service/support`;
      const userWidgets = `${apiManagementBaseUrl}/user-widgets`;

      appConfiguration = {
        ...this.baseAppConfiguration,
        apiUrl: {
          leistungService: leistungServiceUrl,
          bookingService: bookingServiceUrl,
          userCartridgeService: userCartridgeServiceUrl,
          priceRequestService: priceRequestServiceUrl,
          cartridgeGroupService: cartridgeGroupServiceUrl,
          userService: userServiceUrl,
          usageRightServiceUsageRight: usageRightServiceUsageRightUrl,
          usageRightServiceLeistung: usageRightServiceLeistungUrl,
          usageRightServiceOrganization: usageRightServiceOrganizationUrl,
          organizationService: organizationServiceUrl,
          organizationDocumentService: organizationDocumentServiceUrl,
          auditService: auditServiceUrl,
          authorizerService: authorizerServiceUrl,
          notificationServiceBannerNotifications: notificationServiceBannerNotificationsUrl,
          notificationServiceAudiences: notificationServiceAudiencesUrl,
          userNotificationsService: userNotificationsServiceUrl,
          platformNewsService: platformNewsServiceUrl,
          contentService: contentServiceUrl,
          logErrorService: logErrorServiceUrl,
          registration: registrationUrl,
          campaign: campaignUrl,
          furtherQualification: furtherQualificationUrl,
          platformRequest: platformRequestUrl,
          userDownload: userDownloadUrl,
          userRegistration: userRegistrationUrl,
          userOrganization: userOrganizationUrl,
          userPlatformRequest: userPlatformRequestUrl,
          consentService: consentServiceUrl,
          legalDocumentService: legalDocumentServiceUrl,
          usageServiceAggregation: usageServiceAggregation,
          reportService: reportServiceUrl,
          userServiceSupport: userServiceSupport,
          userWidgets,
        },
        auth: this.prepareAuthConfig([
          ...localDevAuthorizedBackendUris,
          `${apiManagementBaseUrl}/*`,
          (this.env.leistungPrototype?.uri || 'https://leistung-prototype.dev-platform-spoke.markant.services/api') +
            '/*',
          'https://api.dev.dva.markant.services/*',
          'https://api.accept.dva.markant.services/*',
          'https://api.demo.dva.markant.services/*',
          'https://api.dva.markant.services/*',

          'https://dps-backend.weu.dev.markhub.markant.services/*',
          'https://dps-backend-accept.weu.dev.markhub.markant.services/*',
          'https://dps-backend.weu.prod.markhub.markant.services/*',

          'https://ara-api.weu.dev.markhub.markant.services/*',
          'https://ara-api-mca.weu.dev.markhub.markant.services/*',
          'https://ara-api-accept.weu.dev.markhub.markant.services/*',
          'https://ara-api-accept-mca.weu.dev.markhub.markant.services/*',
          'https://ara-api.weu.prod.markhub.markant.services/*',
          'https://ara-api-mca.weu.prod.markhub.markant.services/*',

          'https://tukan-api.weu.dev.markhub.markant.services/*',
          'https://tukan-api-mca.weu.dev.markhub.markant.services/*',
          'https://tukan-api-accept.weu.dev.markhub.markant.services/*',
          'https://tukan-api-accept-mca.weu.dev.markhub.markant.services/*',
          'https://tukan-api.weu.prod.markhub.markant.services/*',
          'https://tukan-api-mca.weu.prod.markhub.markant.services/*',

          'https://pelikan-api.weu.dev.markhub.markant.services/*',
          'https://pelikan-api-accept.weu.dev.markhub.markant.services/*',
          'https://pelikan-api.weu.prod.markhub.markant.services/*',

          'https://partnerpool-partner-service.weu.dev.markhub.markant.services/*',
          'https://partnerpool-partner-service-accept.weu.dev.markhub.markant.services/*',
          'https://partnerpool-partner-service.weu.prod.markhub.markant.services/*',
          'https://partnerpool-extended-company-data-service.weu.dev.markhub.markant.services/*',
          'https://partnerpool-extended-company-data-service-accept.weu.dev.markhub.markant.services/*',
          'https://partnerpool-extended-company-data-service.weu.prod.markhub.markant.services/*',

          'https://kolibri-backend.weu.dev.markhub.markant.services/*',
          'https://kolibri-backend-accept.weu.dev.markhub.markant.services/*',
          'https://kolibri-backend.weu.prod.markhub.markant.services/*',

          'https://nis2-frontend-dev.nis2.gwc.dev.markhub.markant.services/*',
          'https://nis2-frontend.nis2.gwc.prod.markhub.markant.services/*',

          'https://aks-rmm.rmm-dev.weu.dev.markhub.markant.services/ramon-api/*',
          'https://aks-rmm.rmm-accept.weu.prod.markhub.markant.services/ramon-api/*',
          'https://aks-rmm.rmm-prod.weu.prod.markhub.markant.services/ramon-api/*',
          'https://commod-iq-staging.germanywestcentral.cloudapp.azure.com/ramon-api/*',
          'https://commod-iq-production.germanywestcentral.cloudapp.azure.com/ramon-api/*',
          'https://rmm-dev.dev.markhub.markant.services/ramon-api/*',
          'https://rmm-accept.prod.markhub.markant.services/ramon-api/*',
          'https://rmm.prod.markhub.markant.services/ramon-api/*',

          'https://backend.dev-lksg.markant.services/*',
          'https://nace-service.dev-lksg.markant.services/*',
          'https://gpc-service.dev-lksg.markant.services/*',
          'https://backend.uat-lksg.markant.services/*',
          'https://nace-service.uat-lksg.markant.services/*',
          'https://gpc-service.uat-lksg.markant.services/*',
          'https://backend.lksg.markant.services/*',
          'https://nace-service.lksg.markant.services/*',
          'https://gpc-service.lksg.markant.services/*',
        ]),
        features: {
          ...(this.baseAppConfiguration.features as Record<AppFeature, string | undefined>),
        },
        logoutPageUrl: this.baseUrl + '/logout',
        publicEndpoints: [
          { route: registrationUrl, httpMethods: [HttpMethod.Post] },
          { route: registrationUrl + '/demo', httpMethods: [HttpMethod.Post] },
          { route: registrationUrl + '/campaign/*', httpMethods: [HttpMethod.Post] },
          { route: campaignUrl + '/*', httpMethods: [HttpMethod.Get] },
          { route: registrationUrl + '/verify-email', httpMethods: [] },
          { route: userRegistrationUrl + '/complete', httpMethods: [] },
          { route: contentServiceUrl + '/footer-links', httpMethods: [] },
          { route: contentServiceUrl + '/terms-of-use', httpMethods: [] },
          { route: contentServiceUrl + '/privacy-notice', httpMethods: [] },
          { route: contentServiceUrl + '/gtc', httpMethods: [] },
          { route: contentServiceUrl + '/robots.txt', httpMethods: [] },
          { route: leistungServiceUrl + '/*?scope=name', httpMethods: [] },
          { route: cartridgeGroupServiceUrl + '/*/name', httpMethods: [] },
          { route: userServiceUrl + '/mfa-select', httpMethods: [] },
          { route: consentServiceUrl + '/public', httpMethods: [] },
        ],
      };
    }
    if (!appConfiguration) {
      throw new Error('AppConfiguration was not initialized!');
    }

    AppConfigService.appConfiguration = appConfiguration;

    return Promise.resolve(appConfiguration);
  }

  static isFeatureEnabled(feature: AppFeature): boolean {
    const featureConfig = AppConfigService.appConfiguration?.features?.[feature];
    if (!featureConfig) {
      return true;
    }

    return featureConfig === 'true';
  }

  private prepareAuthConfig(allowedBackendUris: string[]): AuthConfig {
    let tenantUrl = this.env.auth?.domain || 'https://dev-identity.markant.com';
    let clientId = this.env.auth?.clientId || 'mUg40QiSP2eFKCKkR5cn1Jy4tPIwJmfH';
    let audience = this.env.auth?.apiAudience || 'https://api.dev-platform-spoke.markant.services';

    if (
      this.env.stage !== Stage.PROD &&
      this.iamToggleService.isActive(IAMTenant.MARKANT_CENTRAL) &&
      this.env.iamToggleAuth?.domain &&
      this.env.iamToggleAuth?.clientId &&
      this.env.iamToggleAuth?.apiAudience
    ) {
      tenantUrl = this.env.iamToggleAuth.domain;
      clientId = this.env.iamToggleAuth.clientId;
      audience = this.env.iamToggleAuth.apiAudience;
    }

    const authorizedApis: ApiRouteDefinition[] = [];
    allowedBackendUris.forEach(backendUri =>
      authorizedApis.push({
        uri: backendUri,
        allowAnonymous: true,
        tokenOptions: {
          authorizationParams: {
            audience: audience,
          },
        },
      })
    );

    return {
      domain: tenantUrl,
      clientId: clientId,
      authorizationParams: {
        audience: audience,
        redirect_uri: this.baseUrl,
      },
      useRefreshTokens: true,
      httpInterceptor: {
        allowedList: authorizedApis,
      },
    } as AuthConfig;
  }
}
